import React, { useState, useEffect, useRef } from 'react';
import { TableContainer, Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, TextField, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import TextFieldDynamicVariableTooltip from 'components/Common/TextFieldDynamicVariableTooltip.jsx';
import { Trash2, Info } from 'lucide-react';
import { useSnackbar } from 'contexts/CustomSnackbarContext';

const getNewRow = () => {
  const newRow = { var_name: '', var_value_evaluator: '' };
  return { ...newRow };
}

const DynamicVariables = ({
  initialDynamicVariables = [],
  handleSaveDynamicVariable = () => { },
  handleUpdateDynamicVariable = () => { },
  handleDeleteDynamicVariable = () => { },
  skipOutsideClickCheck,
  responseVariables = {}
}) => {
  const { openSnackbar } = useSnackbar();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const formattedDynamicVariables = initialDynamicVariables?.map(p => ({ ...getNewRow(), ...p }));
  const [newRows, setNewRows] = useState(formattedDynamicVariables ? formattedDynamicVariables : [getNewRow()]);
  const dynamicVariableRefs = useRef(null);

  useEffect(() => {
    setNewRows([...(initialDynamicVariables?.map(p => ({ ...getNewRow(), ...p })) || []), { ...getNewRow() }]);
  }, [initialDynamicVariables]);

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [newRows]);

  const handleRowInputChange = (index, field, value) => {
    let updatedRows = [...newRows];
    if (field === "required") {
      updatedRows[index] = { ...updatedRows[index], [field]: value ? true : false };
    } else {
      updatedRows[index] = { ...updatedRows[index], [field]: value };
    }

    updatedRows = updatedRows.filter((row) => {
      return row.var_name || row.var_value_evaluator;
    });

    const newRow = getNewRow();
    updatedRows.push(newRow);
    setNewRows(updatedRows);
    if (skipOutsideClickCheck) {
      filterValidParams(updatedRows);
    }
  };

  const handleOutsideClick = async (event) => {
    if (dynamicVariableRefs.current && !dynamicVariableRefs.current.contains(event.target)) {
      filterValidParams(newRows);
    }
  };

  const filterValidParams = (updatedRows = []) => {
    let filteredRows = updatedRows.filter((row) => {
      return row.var_name;
    });
  }

  const rowTableCellSx = {
    padding: '5px 10px', margin: 0, fontWeight: "550", fontSize: "11px"
  }
  const inputTableCellSx = {
    padding: '5px 10px',
    fontSize: '11px',
    fontWeight: "400"
  }

  const tooltipSx = {
    backgroundColor: 'var(--background-secondary)',
    borderRadius: '8px',
    padding: '8px 12px',
  }

  const lastResolvedValueSx = {
    textAlign: 'center',
    padding: 0,
    margin: 0,
    maxWidth: '200px',
    overflow: 'hidden'
  }

  const validateRow = (row) => {
    if (!row.var_name.trim()) {
      openSnackbar({ message: 'Key field cannot be empty', severity: 'error' });
      return false;
    }
    if (!row.var_value_evaluator.trim()) {
      openSnackbar({ message: 'Value field cannot be empty', severity: 'error' });
      return false;
    }

    const value = row.var_value_evaluator.trim();
    const validFormat = /^{{current_response_body\.[^}]+}}$/;

    if (!validFormat.test(value)) {
      openSnackbar({
        message: 'Value must be in format: {{current_response_body.key}}. Example: {{current_response_body.email}}',
        severity: 'error'
      });
      return false;
    }

    return true;
  };

  const handleKeyPress = (e, row) => {
    if (e.key === 'Enter') {
      if (!validateRow(row)) return;

      if (row.created_at) {
        handleUpdateDynamicVariable(row.id, row.var_name, row.var_value_evaluator);
      } else {
        handleSaveDynamicVariable(row.var_name, row.var_value_evaluator);
      }
    }
  };

  const handleDeleteClick = (id) => {
    setSelectedRowId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    handleDeleteDynamicVariable(selectedRowId);
    setDeleteDialogOpen(false);
    setSelectedRowId(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setSelectedRowId(null);
  };

  return (
    <Box id='boundary' className="bg-[var(--background-primary)]" ref={dynamicVariableRefs}>
      <Typography variant="body1" sx={{ fontWeight: "500", fontSize: "12px", mb: "4px", color: "var(--text-primary)" }}>
        Post Script Variables
        <Tooltip
          title={
            <span className="text-sm">
              Follow the below instructions to create Post Script Variables:<br /><br />
              1. Use keys from <i><code>{'{{current_response_body}}'}</code></i> to create Post Script Variables.<br />
              2. The correct format is <i><code>{'{{current_response_body.key}}'}</code></i> — for example: <i><code>{'{{current_response_body.email}}'}</code></i>.<br />
              3. Only values starting with <i><code>{'{{current_response_body}}'}</code></i> will be saved.<br />
              4. Avoid using static values here. For static or constant values, use the <strong>Environment Variables</strong>.
            </span>
          }
          placement="bottom"
          arrow
          componentsProps={{
            tooltip: {
              sx: {
                backgroundColor: 'var(--background-secondary)', // e.g., slate-800
                borderRadius: '8px',
                padding: '8px 12px',
              },
            },
            arrow: {
              sx: {
                color: 'var(--background-secondary)', // match tooltip background
              }
            }
          }}
        >
          <Info size={16} className="ml-2 cursor-pointer" />
        </Tooltip>
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: "500", fontSize: "11px", mb: "4px", color: "var(--text-secondary)" }}>
        Key must be unique • Press enter to add/update post script variable
      </Typography>
      <TableContainer className='table-layout' sx={{ borderRadius: '5px', height: '220px', overflowY: 'auto', backgroundColor: 'var(--background-primary)', border: "1px solid var(--borderColor)" }}>
        <Table size='small' className='bg-[var(--background-primary)]' aria-label="simple" sx={{ border: 'none', padding: '0' }}>
          <TableHead>
            <TableRow sx={{ alignItems: 'center' }}>
              <TableCell sx={rowTableCellSx} key="key">Key</TableCell>
              <TableCell sx={rowTableCellSx} key="value">Value</TableCell>
              <TableCell sx={rowTableCellSx} key="last_resolved_value">Last Resolved Value</TableCell>
              <TableCell sx={rowTableCellSx} key="datatype">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {newRows.map((row, index) => (
              <TableRow key={index} sx={{ border: 'none' }}>
                <TableCell sx={{ textAlign: 'center', padding: 0, margin: 0 }} key={`key_${index}`}>
                  <TextField
                    variant="outlined"
                    fullWidth
                    size='small'
                    placeholder='Key'
                    value={row.var_name}
                    onChange={e => handleRowInputChange(index, 'var_name', e.target.value)}
                    onKeyDown={(e) => handleKeyPress(e, row)}
                    disabled={row.created_at}
                    InputProps={{
                      classes: { notchedOutline: 'no-border' },
                      className: 'custom-textfield',
                    }}
                    sx={{
                      height: '27px',
                      '& .MuiInputBase-input': inputTableCellSx
                    }}
                  />
                </TableCell>
                <TableCell sx={{ textAlign: 'center', padding: 0, margin: 0 }} key={`value_${index}`}>
                  <TextFieldDynamicVariableTooltip
                    variant="outlined"
                    fullWidth
                    size='small'
                    placeholder='Value'
                    value={row.var_value_evaluator}
                    onChange={e => handleRowInputChange(index, 'var_value_evaluator', e.target.value)}
                    onKeyDown={(e) => handleKeyPress(e, row)}
                    showContextMenu={false}
                    InputProps={{
                      classes: { notchedOutline: 'no-border' },
                      className: 'custom-textfield',
                    }}
                    sx={{
                      height: '27px',
                      '& .MuiInputBase-input': inputTableCellSx
                    }}
                    responseVariables={responseVariables}
                  />
                </TableCell>
                <TableCell sx={lastResolvedValueSx} key={`last_resolved_value_${index}`}>
                  <Tooltip
                    title={
                      <span className='text-[11px]'>{row?.last_resolved_value || ""}</span>
                    }
                    placement='top'
                    componentsProps={{
                      tooltip: { sx: tooltipSx }
                    }}
                  >
                    <span className='text-[11px] truncate block px-2 text-[var(--text-secondary)]'>
                      {row?.last_resolved_value || ""}
                    </span>
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ textAlign: 'center', padding: 0, margin: 0 }} key={`delete_${index}`}>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                    {row.id && (
                      <Tooltip title="Delete">
                        <Trash2
                          size={14}
                          className="cursor-pointer text-[var(--text-secondary)] hover:text-red-400 transition-colors duration-200"
                          onClick={() => handleDeleteClick(row.id)}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        PaperProps={{
          sx: {
            backgroundColor: 'var(--background-primary)',
            color: 'var(--text-primary)',
            minWidth: '300px'
          }
        }}
      >
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 500 }}>Delete Variable</DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '14px', color: 'var(--text-secondary)' }}>
            Are you sure you want to delete this variable? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button
            onClick={handleDeleteCancel}
            variant='outlined'
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DynamicVariables;
